import { useMutation, useQuery } from '@tanstack/react-query'
import AXIOS_INSTANCE from '../api/axiosInstance'
import { queryClient } from '../store/provider/TanStackProvider'
import { InventoryMeta, ProductInventoryResponse, SingleProductInventoryResponse } from '../types/inventory.type'

type Props = {
  id?: string
  query?: string
  limit?: string | number
  page?: string | number
  sort_by?: string
  sort_dir?: 'asc' | 'desc'
  product_id?: string
  stock_location?: string
  stat?: boolean
  /** Prefer main_pharmacy_only; is_main_pharmacy kept as alias for callers. */
  is_main_pharmacy?: boolean
  main_pharmacy_only?: boolean
  facility_id?: string
  /** When false, list query does not run (e.g. waiting for facility_id). */
  enabled?: boolean
}

export const useProductInventory = (arg: Props = {}) => {
  const { id, facility_id, is_main_pharmacy, main_pharmacy_only, enabled = true } = arg
  const { query, limit, page, sort_by, sort_dir, product_id, stock_location, stat } = arg
  const mainPharmacyOnly = Boolean(main_pharmacy_only ?? is_main_pharmacy)

  const { data: inventories, isLoading: inventoriesIsLoading } = useQuery({
    enabled,
    queryFn: async () => {
      const { data } = await AXIOS_INSTANCE.get<ProductInventoryResponse>('product_inventories', {
        params: {
          ...(sort_dir && { sort_dir }),
          ...(page && { 'page[number]': page }),
          ...(limit && { 'page[size]': limit }),
          ...(sort_by && { sort_by }),
          ...(product_id && { product_id }),
          ...(stock_location && { stock_location }),
          ...(query && { query }),
          ...(stat && { stat }),
          ...(facility_id && { facility_id }),
          // API expects main_pharmacy_only (is_main_pharmacy is for inventory_locations).
          ...(mainPharmacyOnly && { main_pharmacy_only: true })
        }
      })
      return data
    },
    queryKey: [
      'product_inventories',
      {
        id,
        limit,
        stock_location,
        page,
        product_id,
        query,
        sort_by,
        sort_dir,
        facility_id,
        main_pharmacy_only: mainPharmacyOnly
      }
    ]
  })

  const { data: singleInventory, isLoading: singleInventoryIsLoading } = useQuery({
    enabled: !!id,
    queryFn: async () => {
      const { data } = await AXIOS_INSTANCE.get<SingleProductInventoryResponse>(`product_inventories/${id}`)
      return data
    },
    queryKey: ['product_inventories', { id }]
  })

  const { mutateAsync: addInventory } = useMutation({
    mutationFn: async (formVal: any) => {
      const { data } = await AXIOS_INSTANCE.post(`product_inventories`, formVal, {
        headers: { 'Content-Type': 'application/vnd.api+json' }
      })
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['product_inventories'] })
      queryClient.invalidateQueries({ queryKey: ['products'] })
    }
  })

  const { mutateAsync: updateInventory } = useMutation({
    mutationFn: async ({ id, formVal }: { id: string; formVal: any }) => {
      const { data } = await AXIOS_INSTANCE.patch(`product_inventories/${id}`, formVal, {
        headers: { 'Content-Type': 'application/vnd.api+json' }
      })
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['product_inventories'] })
      queryClient.invalidateQueries({ queryKey: ['products'] })
    }
  })

  return {
    addInventory,
    updateInventory,
    includedData: inventories?.included,
    inventories: inventories?.data,
    inventoriesIsLoading,
    meta: inventories?.meta as InventoryMeta,
    pagination: inventories?.meta?.pagination,
    singleInventory: singleInventory?.data,
    singleInventoryIsLoading
  }
}

export default useProductInventory
